// `ensureConversation` is the bridge between the server-minted
// `conversationId` (returned by POST /v1/conversations/request) and
// the local Dexie store that powers the ConversationListPanel +
// ChatRoom.
//
// Background: the chat surfaces read from the IndexedDB
// `conversations` table via packages/ui's `useConversations` /
// `useMessages` hooks. When the user starts a chat through the
// NewChatModal we get back a fresh `conversationId` from the BE,
// but there is no matching local row yet — without one, ChatRoom
// renders an empty/error state because `useMessages` can't find
// the conversation. This helper inserts a placeholder row so the
// UI has something to render until the first message lands and
// hydrates the rest of the metadata.
//
// The operation is idempotent (uses `Dexie.put` keyed on `id`), so
// re-opening the same chat (request(A→B) twice) is a no-op rather
// than a duplicate-key error.

import { db } from '@core/persistence/db';

interface EnsureConversationParams {
  readonly conversationId: string;
  readonly counterpartDisplayName: string;
  // For now every conversation surfaced from the New chat flow is a
  // 1:1 ("direct"). Group chats will route through a different
  // creation path (POST /v1/conversations + Welcome fan-out).
  readonly kind?: 'direct' | 'group';
  // Peer account_id — captured so the MLS-invite flow in
  // mlsConversation.ts can fetch the peer's KeyPackage + device list
  // before the first application message goes out. Required for
  // direct chats; ignored for groups.
  readonly peerAccountId?: string;
}

export async function ensureConversation(params: EnsureConversationParams): Promise<void> {
  const existing = await db.conversations.get(params.conversationId);
  const now = Date.now();

  if (existing) {
    // Keep the local row's name in sync with whatever the directory
    // currently reports, in case the counterpart renamed themselves
    // between this user's two visits.
    const updates: {
      name?: string;
      peerAccountId?: string;
      deletedAt?: number;
      orphanedAt?: number;
    } = {};
    if (
      params.counterpartDisplayName.length > 0 &&
      existing.name !== params.counterpartDisplayName
    ) {
      updates.name = params.counterpartDisplayName;
    }
    // Backfill peerAccountId on rows created before this field existed.
    if (params.peerAccountId && !existing.peerAccountId) {
      updates.peerAccountId = params.peerAccountId;
    }
    // RE-START AFTER DELETE: un-hide a soft-deleted row (`deletedAt = 0` is the
    // canonical "visible" marker; the list filters `(deletedAt ?? 0) === 0`).
    // When you delete a 1:1 then start it again, `getOrCreateMlsConversation`
    // reuses the still-live MLS group and returns the SAME conversation id, so
    // the invite reads `skipped: already_invited` — but the local row is still
    // soft-deleted, so the chat never reappears. Clearing `deletedAt` resurfaces
    // it (history intact) so the re-started chat shows up.
    if ((existing.deletedAt ?? 0) !== 0) {
      updates.deletedAt = 0;
    }
    // RE-START AFTER SERVER-ORPHAN: this runs after POST /v1/conversations/request
    // succeeded, i.e. the server just authoritatively confirmed the conversation
    // is live. That overrides a stale `orphanedAt` from an earlier transient 404
    // streak — clear it so the deliberately-reopened chat is never left hidden.
    if ((existing.orphanedAt ?? 0) !== 0) {
      updates.orphanedAt = 0;
    }
    if (Object.keys(updates).length > 0) {
      await db.conversations.update(params.conversationId, updates);
    }
    return;
  }

  await db.conversations.put({
    id: params.conversationId,
    kind: params.kind ?? 'direct',
    name: params.counterpartDisplayName,
    createdAt: now,
    ...(params.peerAccountId ? { peerAccountId: params.peerAccountId } : {}),
    // lastMessageAt is intentionally unset until the first message
    // arrives — leaving it nil keeps the row out of the "active"
    // sort bucket in ConversationListPanel.
  });
}

export interface EnsureGroupConversationParams {
  readonly conversationId: string;
  readonly displayName: string;
  /** Sorted unique peer account IDs (base64url), excluding local user. */
  readonly memberAccountIds: readonly string[];
  /** accountId → display name captured at create time. */
  readonly memberDisplayNames?: Readonly<Record<string, string>>;
  /** deviceIdHex → display name for typing + bubble labels. */
  readonly memberDeviceNames?: Readonly<Record<string, string>>;
  /** deviceIdHex → account id for avatar / directory lookup. */
  readonly memberDeviceAccounts?: Readonly<Record<string, string>>;
  /** Group avatar (data/remote URL) chosen at create time. */
  readonly avatarUri?: string;
  /** Stable media id of the group avatar. MUST be persisted: `avatarUri` is a
   *  short-lived presigned URL that expires, and the avatar is only ever
   *  re-resolved (on hydration / reload) from this id. Without it the group
   *  icon vanishes once the presigned URL expires or the row churns on a roster
   *  change. */
  readonly avatarMediaId?: string;
  /** C3: the group OWNER (creator) account id — bootstraps role authority. */
  readonly ownerAccountId?: string;
}

/**
 * Insert or update a local group row after MLS create.
 */
export async function ensureGroupConversation(
  params: EnsureGroupConversationParams,
): Promise<void> {
  const peerIds = [...params.memberAccountIds].sort();
  const existing = await db.conversations.get(params.conversationId);
  const now = Date.now();

  if (existing) {
    const name =
      params.displayName.length > 0 ? params.displayName : (existing.name ?? params.displayName);
    const existingPeers = existing.memberAccountIds
      ? [...existing.memberAccountIds].sort().join(',')
      : '';
    const nextPeers = peerIds.join(',');
    const nextDeviceNames = params.memberDeviceNames
      ? JSON.stringify(params.memberDeviceNames)
      : '';
    const existingDeviceNames = existing.memberDeviceNames
      ? JSON.stringify(existing.memberDeviceNames)
      : '';
    // RE-CREATE AFTER DELETE: a soft-deleted row is NEVER "unchanged" — it must
    // be resurrected so the re-created group (deduped onto the same id by the
    // same-members dedup) reappears in the list. The list filters
    // `(deletedAt ?? 0) === 0`, so we clear it on the put below.
    const wasDeleted = (existing.deletedAt ?? 0) !== 0;
    const unchanged =
      !wasDeleted &&
      existing.kind === 'group' &&
      existing.name === name &&
      existingPeers === nextPeers &&
      existingDeviceNames === nextDeviceNames &&
      existing.peerAccountId === undefined;
    if (unchanged) return;

    const { peerAccountId: _removed, ...rest } = existing;
    await db.conversations.put({
      ...rest,
      kind: 'group',
      // DURABLE group marker: this row is now unambiguously a group and must
      // never be collapsed into a same-peer 1:1 (see `isRealGroup`). Set even
      // for a single-peer group, which is otherwise roster-identical to a 1:1.
      explicitGroup: true,
      name,
      // Un-hide on re-create (delete + re-create with the same members). Clear
      // BOTH hide markers: `...rest` above carried over the old row's state, so a
      // stale `orphanedAt` (from a prior server-404 streak) would otherwise keep
      // the freshly re-created group hidden. This deliberate re-create is a live
      // server-confirmed action, so the group must be visible.
      deletedAt: 0,
      orphanedAt: 0,
      memberAccountIds: peerIds,
      ...(params.memberDisplayNames !== undefined
        ? {
            memberDisplayNames: {
              ...(existing.memberDisplayNames ?? {}),
              ...params.memberDisplayNames,
            },
          }
        : {}),
      ...(params.memberDeviceNames !== undefined
        ? {
            memberDeviceNames: {
              ...(existing.memberDeviceNames ?? {}),
              ...params.memberDeviceNames,
            },
          }
        : {}),
      ...(params.memberDeviceAccounts !== undefined
        ? {
            memberDeviceAccounts: {
              ...(existing.memberDeviceAccounts ?? {}),
              ...params.memberDeviceAccounts,
            },
          }
        : {}),
      ...(params.avatarUri !== undefined
        ? { avatarUri: params.avatarUri }
        : existing.avatarUri !== undefined
          ? { avatarUri: existing.avatarUri }
          : {}),
      // Persist the stable avatar media id so the icon survives presigned-URL
      // expiry + roster-change churn (re-resolved from this on every hydration).
      ...(params.avatarMediaId !== undefined
        ? { avatarMediaId: params.avatarMediaId }
        : existing.avatarMediaId !== undefined
          ? { avatarMediaId: existing.avatarMediaId }
          : {}),
      // C3: owner is set once (creator); never override an existing owner.
      ...(existing.ownerAccountId !== undefined
        ? { ownerAccountId: existing.ownerAccountId }
        : params.ownerAccountId !== undefined && params.ownerAccountId.length > 0
          ? { ownerAccountId: params.ownerAccountId }
          : {}),
    });
    return;
  }

  await db.conversations.put({
    id: params.conversationId,
    kind: 'group',
    // DURABLE group marker (see `isRealGroup`) — set at creation so a single-peer
    // group is never merged into the same-peer 1:1.
    explicitGroup: true,
    name: params.displayName,
    memberAccountIds: peerIds,
    ...(params.memberDisplayNames !== undefined
      ? { memberDisplayNames: { ...params.memberDisplayNames } }
      : {}),
    ...(params.memberDeviceNames !== undefined
      ? { memberDeviceNames: { ...params.memberDeviceNames } }
      : {}),
    ...(params.memberDeviceAccounts !== undefined
      ? { memberDeviceAccounts: { ...params.memberDeviceAccounts } }
      : {}),
    ...(params.avatarUri !== undefined ? { avatarUri: params.avatarUri } : {}),
    ...(params.avatarMediaId !== undefined ? { avatarMediaId: params.avatarMediaId } : {}),
    ...(params.ownerAccountId !== undefined && params.ownerAccountId.length > 0
      ? { ownerAccountId: params.ownerAccountId }
      : {}),
    createdAt: now,
  });
}
